import { LoadState } from "@redotech/react-util/load";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { TagGroup } from "@redotech/redo-model/support/tag-group-zod-schema";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { memo, useState } from "react";
import { TagInputFields } from "./tag-fields";

// Tag like object, less restrictive on "source" so we can reuse this component
export type TagWithId = {
  name: string;
  pillTheme: PillTheme;
  tagId: string;
  source: string;
};

export const CreateTagModal = memo(function CreateTagModal({
  cancelClicked,
  tagsLoad,
  handleCreateTag,
  tagGroups = [],
}: {
  cancelClicked(): void;
  tagsLoad: LoadState<TagWithId[]>;
  handleCreateTag: ({
    name,
    pillTheme,
    tagGroupId,
  }: {
    name: string;
    pillTheme: PillTheme;
    tagGroupId?: string | null;
  }) => Promise<void>;
  tagGroups?: TagGroup[];
}) {
  const existingTagNames = new Set(
    tagsLoad.value?.map((tag) => tag.name.toLowerCase()),
  );

  const [pillTheme, setPillTheme] = useState<PillTheme>(
    PillTheme.PRIMARY_LIGHT,
  );
  const [name, setName] = useState("");
  const [selectedTagGroup, setSelectedTagGroup] = useState<string | null>(null);

  async function handleSubmit() {
    await handleCreateTag({ name, pillTheme, tagGroupId: selectedTagGroup });
  }

  const nameConflict = existingTagNames.has(name.toLowerCase());

  return (
    <RedoModal
      isOpen
      onModalCloseRequested={cancelClicked}
      primaryButton={{
        text: "Create",
        onClick: handleSubmit,
        disabled: !name || nameConflict,
      }}
      secondaryButton={{ text: "Cancel", onClick: cancelClicked }}
      title="Create tag"
    >
      <TagInputFields
        name={name}
        pillTheme={pillTheme}
        selectedTagGroup={selectedTagGroup}
        setName={setName}
        setPillTheme={setPillTheme}
        setSelectedTagGroup={setSelectedTagGroup}
        showNameError={nameConflict}
        tagGroups={tagGroups}
      />
    </RedoModal>
  );
});
